import moment from "moment";
import companyService from "@/services/company";
import warehouseFiltersMixin from "@/mixins/warehouse/warehouseFiltersMixin";
import { formatRange, formatShortDate } from "@/components/report/shared/periods";
import { DEFAULT_WAREHOUSE_SCREEN, buildWarehouseMenu, buildWarehouseScreens } from "./warehouseMenu";
import {
  LEGACY_UI_VALUE,
  MANAGED_WAREHOUSE_QUERY,
  WAREHOUSE_QUERY,
  buildWarehousePeriodPresets,
  parseRange,
} from "./warehouseQuery";

const ISO = "YYYY-MM-DD";
const EMPTY_WAREHOUSE = { id: "", name: "", location: "", notes: "" };

/**
 * Zajedničko stanje ljuske magacina (računar i telefon). Izbori u traci (magacini, period, smene, dobavljači,
 * status, pretraga…) žive u store-u `warehouse/filters`, isto kao do sada, pa stari tabovi rade bez izmena;
 * ljuska ih samo menja i drži izabrani tab, učitavanje magacina i adresu (?t=tab&from=&to=).
 */
export default {
  mixins: [warehouseFiltersMixin],
  data: () => ({
    warehouses: [],
    warehousesLoading: true,
    companiesLoaded: false,
    warehouseDialog: false,
    editedWarehouse: { ...EMPTY_WAREHOUSE },
  }),
  computed: {
    filters() {
      return this.$store.state.warehouse.filters;
    },
    suppliers() {
      return this.$store.state.supplier.suppliers || [];
    },
    presets() {
      return buildWarehousePeriodPresets();
    },
    screens() {
      return buildWarehouseScreens((key, params) => this.$t(key, params));
    },
    menuGroups() {
      return buildWarehouseMenu((key, params) => this.$t(key, params)).map((group) => ({
        ...group,
        items: group.items.map((key) => ({ key, ...this.screens[key] })),
      }));
    },
    menuKeys() {
      return this.menuGroups.flatMap((group) => group.items.map((item) => item.key));
    },
    currentKey() {
      const wanted = this.$route.query[WAREHOUSE_QUERY.tab];
      if (this.menuKeys.includes(wanted)) {
        return wanted;
      }
      return this.homeWithoutTab ? null : DEFAULT_WAREHOUSE_SCREEN;
    },
    screen() {
      return this.currentKey ? this.screens[this.currentKey] : null;
    },
    /** Izbori u traci za otvoreni ekran (prazno na početnoj telefona). */
    screenFilters() {
      return this.screen ? this.screen.filters : {};
    },
    /** Telefon: bez ?t= je početna sa listom tabova, pa se i podrazumevani tab upisuje u adresu. */
    homeWithoutTab() {
      return false;
    },
    /** Magacini i vrsta prikaza jela su učitani: tek tada tabovi mogu da učitaju svoje podatke. */
    ready() {
      return this.companiesLoaded && !this.warehousesLoading;
    },
    from() {
      return this.filters.dates.from;
    },
    to() {
      return this.filters.dates.to;
    },
    search: {
      get() {
        return this.filters.search;
      },
      set(value) {
        this.setFilterValue("search", value);
      },
    },
    selectedWarehouseIds() {
      return (this.filters.warehouse || []).map((warehouse) => warehouse.id);
    },
    selectedSupplierIds() {
      return (this.filters.suppliers || []).map((supplier) => supplier.id);
    },
    // ---- liste za izbore u traci: { id, name } (id `null` = "svi")
    warehouseItems() {
      return this.warehouses.map(({ id, name }) => ({ id, name }));
    },
    supplierItems() {
      return this.suppliers.map(({ id, name }) => ({ id, name }));
    },
    viewItems() {
      return this.showItems.map((item) => ({ id: item.value, name: item.text }));
    },
    statusItems() {
      return this.statusTypes.map((item) => ({ id: item.value, name: item.name }));
    },
    docTypeItems() {
      const kind = this.screenFilters.docType;
      if (kind === "transaction") {
        return this.transactionTypes.map((item) => ({ id: item.value, name: item.text }));
      }
      const list = kind === "offer" ? this.offerTypeItems : this.documentTypeItems;
      return list.map((item) => ({ id: item.value, name: item.name }));
    },
    /** Namirnice za filter po šifri (pune ih tabovi Dopuna i Transakcije) + "sve". */
    groceryItems() {
      const groceries = (this.$store.state.warehouse.codeFilterGroceries || []).filter((item) => item.code);
      return [
        { id: null, name: this.$t("all") },
        ...groceries.map((item) => ({ id: item.code, name: `${item.code} – ${item.name}` })),
      ];
    },
    /** Ekran traži izabran magacin (Magacini i Ponude/fakture ne traže). */
    needsWarehouse() {
      return Boolean(this.screenFilters.warehouse) && !this.filters.warehouse.length;
    },
    warehouseLabel() {
      const selected = this.filters.warehouse || [];
      if (selected.length === 1) {
        return this.toLatin(selected[0].name);
      }
      if (selected.length && selected.length === this.warehouses.length) {
        return this.$t("warehouse_shell.all_warehouses_n", { n: selected.length });
      }
      return selected.length ? `${this.toLatin(selected[0].name)} +${selected.length - 1}` : "";
    },
    periodLong() {
      if (this.from !== this.to) {
        return formatRange(this.from, this.to);
      }
      const weekday = this.localeDate(this.from, { weekday: "long" });
      return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)}, ${formatShortDate(this.from, true)}`;
    },
    headerSubtitle() {
      const parts = [];
      if (this.screenFilters.period) {
        parts.push(this.periodLong);
      }
      if (this.screenFilters.warehouse && this.warehouseLabel) {
        parts.push(this.warehouseLabel);
      }
      return parts.length ? parts.join(" · ") : this.$t("warehouse_management_system");
    },
    /** Stanje koje ide u adresu (današnji dan i podrazumevani tab se izostavljaju). */
    urlQuery() {
      const today = moment().format(ISO);
      const query = {};
      if (this.currentKey && (this.homeWithoutTab || this.currentKey !== DEFAULT_WAREHOUSE_SCREEN)) {
        query[WAREHOUSE_QUERY.tab] = this.currentKey;
      }
      if (this.screenFilters.period && !(this.from === today && this.to === today)) {
        query[WAREHOUSE_QUERY.from] = this.from;
        query[WAREHOUSE_QUERY.to] = this.to;
      }
      return query;
    },
  },
  watch: {
    urlQuery: {
      handler(query) {
        this.writeUrl(query);
      },
      deep: true,
    },
    currentKey(key) {
      if (key) {
        this.onScreenChanged(key);
      }
    },
    // dobavljači stižu sa korisnikom: izbor je svi, jer dokumenti sa dobavljačem inače nestaju iz Naručeno
    suppliers() {
      this.selectAllSuppliers();
    },
  },
  created() {
    const range = parseRange(
      this.$route.query[WAREHOUSE_QUERY.from],
      this.$route.query[WAREHOUSE_QUERY.to]
    );
    if (range) {
      this.$store.commit("warehouse/SET_DATE", range);
    }
    if (this.currentKey) {
      this.onScreenChanged(this.currentKey);
    }
    this.selectAllSuppliers();
    this.loadCompanies();
    this.loadWarehouses();
  },
  mounted() {
    this.writeUrl(this.urlQuery);
  },
  methods: {
    // ------------------------------------------------------------ učitavanje
    /** Vrsta prikaza jela po većini firmi: grupne firme → "Jela iz grupa", inače "Jela u obrocima". */
    async loadCompanies() {
      let showType = "daily";
      try {
        const res = await companyService.getCompanies();
        const companies = res.data || [];
        const groups = companies.filter((company) => company.type === "group").length;
        showType = groups > companies.length - groups ? "daily-group" : "daily";
      } catch (error) {
        console.error("Error loading companies:", error);
      } finally {
        this.setFilterValue("showType", showType);
        this.companiesLoaded = true;
      }
    },
    async loadWarehouses() {
      this.warehousesLoading = true;
      try {
        await this.$store.dispatch("warehouse/fetchItems");
        this.warehouses = [...this.$store.state.warehouse.items];
        // izbor iz prethodnog otvaranja ostaje ako magacini još postoje, inače svi
        const kept = this.warehouses.filter((warehouse) => this.selectedWarehouseIds.includes(warehouse.id));
        this.setFilterValue("warehouse", kept.length ? kept : [...this.warehouses]);
      } catch (error) {
        console.error("Error loading warehouses:", error);
        this.showToast?.(this.$t("error_loading_warehouses"));
      } finally {
        this.warehousesLoading = false;
      }
    },
    selectAllSuppliers() {
      if (this.suppliers.length && !this.filters.suppliers.length) {
        this.setFilterValue("suppliers", [...this.suppliers]);
      }
    },
    onWarehouseCreated(warehouse) {
      this.warehouses.push(warehouse);
      this.setFilterValue("warehouse", [...this.warehouses]);
      this.editedWarehouse = { ...EMPTY_WAREHOUSE };
    },

    // ------------------------------------------------------------ izbori
    setFilterValue(key, value) {
      this.$store.commit("warehouse/SET_FILTER", { key, value });
    },
    setWarehouseIds(ids) {
      this.setFilterValue(
        "warehouse",
        this.warehouses.filter((warehouse) => ids.includes(warehouse.id))
      );
    },
    setSupplierIds(ids) {
      this.setFilterValue(
        "suppliers",
        this.suppliers.filter((supplier) => ids.includes(supplier.id))
      );
    },
    setGroceryCode(code) {
      const grocery = (this.$store.state.warehouse.codeFilterGroceries || []).find((item) => item.code === code);
      this.setFilterValue("groceryCode", grocery || null);
    },
    setPeriod({ from, to }) {
      if (from === this.from && to === this.to) {
        return;
      }
      this.$store.commit("warehouse/SET_DATE", [from, to]);
    },
    setShifts(shifts) {
      this.setFilterValue("selectedShifts", shifts);
    },
    shiftDay(step) {
      if (this.screenFilters.period !== "range") {
        return;
      }
      const move = (date) => moment(date, ISO).add(step, "day").format(ISO);
      this.setPeriod({ from: move(this.from), to: move(this.to) });
    },
    /** Stanje (Popis zaliha) se uvek gleda za današnji dan, kao u staroj stranici. */
    onScreenChanged(key) {
      if (key === "inventory") {
        const today = moment().format(ISO);
        this.$store.commit("warehouse/SET_DATE", [today, today]);
      }
    },

    // ------------------------------------------------------------ radnje
    openScreen(key) {
      if (!this.menuKeys.includes(key) || key === this.currentKey) {
        return;
      }
      this.$router.push({ query: { ...this.$route.query, [WAREHOUSE_QUERY.tab]: key } }).catch(() => {});
    },
    reload() {
      this.$store.commit("warehouse/TRIGGER_REFRESH");
    },
    print() {
      this.$store.commit("SET_PRINTING");
    },
    excel() {
      this.$store.commit("SET_EXCEL");
    },
    /** Stara stranica (tabovi): ?ui=stari. */
    openLegacy() {
      this.$router.replace({ query: { ...this.$route.query, [WAREHOUSE_QUERY.legacy]: LEGACY_UI_VALUE } }).catch(() => {});
    },
    writeUrl(query) {
      const current = this.$route.query;
      const kept = Object.keys(current).reduce((acc, key) => {
        if (!MANAGED_WAREHOUSE_QUERY.includes(key)) {
          acc[key] = current[key];
        }
        return acc;
      }, {});
      const next = { ...kept, ...query };
      const same =
        Object.keys(next).length === Object.keys(current).length &&
        Object.keys(next).every((key) => String(current[key]) === String(next[key]));
      if (!same) {
        this.$router.replace({ query: next }).catch(() => {});
      }
    },
  },
};
